/**
 * Builds API insert payloads for locally persisted auth-status change comments.
 * @module generatePayloadForAuthStatusComments
 */
import { isValidAuthStatusChangeComments } from "../../../../../data-model/authStatusChangeComments";
import {
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../../../data-model/medicalPaOrdersModel";
import { getSavedAuthStatusChangeCommentsFromLocalStorage } from "../../../../singleOrderLayout/utils/helperForAuthStatusCommentsInLocalStorage";

/**
 * Maps saved auth-status change comments to insert rows, attaching `patient_id`
 * and primary coverage fields (`plan_name`, `payer_name`, `line_of_business`)
 * on `additional_info`. Missing coverage fields are sent as empty strings so
 * the API always receives keys (backend no longer resolves payer itself).
 *
 * @param generalOrderDetails - Order whose demographics supply `patient_id`
 * @param coverages - Order coverages; primary tier supplies plan, payer, and LOB
 * @returns Comment rows ready for insert, or `undefined` when storage is empty or invalid
 */
export const generatePayloadForAuthStatusComments = (
  generalOrderDetails: GeneralOrderDetails,
  coverages: SingleCoverage[],
) => {
  const primaryCoverageDetails =
    coverages?.find((coverage) => coverage.coverage_type === "primary") ?? {};
  const plan_name = primaryCoverageDetails.plan_name ?? "";
  const payer_name = primaryCoverageDetails.payer_name ?? "";
  const line_of_business = primaryCoverageDetails.line_of_business ?? "";
  const comments = getSavedAuthStatusChangeCommentsFromLocalStorage(
    generalOrderDetails?.order_id ?? "",
  );
  if (!comments || !isValidAuthStatusChangeComments(comments ?? [])) {
    return;
  }

  const payload = comments.map((comment) => {
    const additionalInfo = {
      ...(comment.additional_info ?? {}),
      plan_name,
      line_of_business,
      payer_name,
    };

    return {
      ...comment,
      additional_info: additionalInfo,
      patient_id:
        generalOrderDetails?.demographics?.patient_id ??
        generalOrderDetails?.demographics?.patient_mrn ??
        "",
    };
  });

  return payload;
};
